import { useEffect, useState, useMemo } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { getUsersAdmin, getContestProblems, postContestProblems } from '../../utils/api';
import Loading from '../loading';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Select, MenuItem, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemText, Typography, Box, Autocomplete, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import UndoIcon from '@mui/icons-material/Undo';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';

const ALLOWED_COLORS = ['black', 'blue', 'brown', 'green', 'orange', 'pink', 'red', 'yellow'];

const DISTINCT_COLORS = ['#F44336', '#9C27B0', '#3F51B5', '#03A9F4', '#009688', '#4CAF50', '#CDDC39', '#FFC107', '#FF5722', '#795548', '#607D8B', '#E91E63', '#673AB7'];

const getClassColor = (str) => {
  if (!str) return 'transparent';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash % DISTINCT_COLORS.length);
  return DISTINCT_COLORS[index];
};

const compactInputStyle = {
  '& .MuiInputBase-input': { 
    fontSize: { xs: '0.6rem', md: '0.9rem' }, 
    padding: { xs: '4px 0 5px', md: '6px 0 7px' }
  },
  '& .MuiInput-root': {
    marginTop: 0
  }
};

const DebouncedTextField = ({ value, onChange, ...props }) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, 200);
    return () => clearTimeout(handler);
  }, [localValue, onChange, value]);

  return (
    <TextField 
      {...props} 
      value={localValue} 
      onChange={(e) => setLocalValue(e.target.value)} 
      variant="standard"
      sx={compactInputStyle}
    />
  );
};

const DebouncedAutocomplete = ({ value, options, onChange, renderInput }) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [localValue, onChange, value]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      value={localValue}
      onInputChange={(_, newVal) => setLocalValue(newVal)}
      onChange={(_, newVal) => setLocalValue(newVal || '')}
      renderInput={renderInput}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box 
            key={key} 
            component="li" 
            {...optionProps} 
            sx={{ 
                fontSize: { xs: '0.75rem', md: '1rem' },      
                padding: { xs: '4px 8px !important', md: '8px 16px !important' }, 
                minHeight: 'auto !important'   
            }}
          >
            <Box 
                component="span" 
                sx={{ 
                    width: 10, height: 10, 
                    bgcolor: getClassColor(option), 
                    borderRadius: '50%', 
                    mr: 1,
                    flexShrink: 0 
                }} 
            />
            {option}
          </Box>
        );
      }}
      slotProps={{ listbox: { sx: { padding: 0 } } }}
    />
  );
};

const EditProblems = () => {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const [accessToken, setAccessToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [problems, setProblems] = useState([]);
  const [original, setOriginal] = useState([]);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (isAuthenticated) getAccessTokenSilently().then(setAccessToken);
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (accessToken) getUsersAdmin(accessToken).then(setIsAdmin);
  }, [accessToken]);

  useEffect(() => {
    if (!authLoading && accessToken && isAdmin) {
      getContestProblems(accessToken).then((data) => {
        if (data) {
          setProblems(data);
          setOriginal(JSON.parse(JSON.stringify(data)));
        }
        setLoadingData(false);
      });
    }
  }, [authLoading, accessToken, isAdmin]);

  const activeRows = useMemo(() => problems.filter(r => !r.deleted), [problems]);
  const deletedRows = useMemo(() => problems.filter(r => r.deleted), [problems]);
  const existingClasses = useMemo(() => {
    const classes = new Set(original.map(p => p.clazz));
    return Array.from(classes).sort();
  }, [original]);

  const validationErrors = useMemo(() => {
    const errors = {}; 
    const uniquenessMap = {}; 
    activeRows.forEach(row => {
      const clazzStr = (row.clazz || '').trim().toLowerCase();
      const key = `${clazzStr}|${row.nr}`;
      uniquenessMap[key] = (uniquenessMap[key] || 0) + 1;
    });
    activeRows.forEach(row => {
      const rowErrors = [];
      const clazzStr = (row.clazz || '').trim();
      if (!clazzStr) rowErrors.push("Class is required");
      if (!row.nr) rowErrors.push("Nr is required");
      if (!row.description || row.description.trim() === '') rowErrors.push("Description is required");
      const key = `${clazzStr.toLowerCase()}|${row.nr}`;
      if (uniquenessMap[key] > 1) {
        rowErrors.push("Duplicate Class + Nr combination");
      }
      if (rowErrors.length > 0) errors[row.id] = rowErrors;
    });
    return errors;
  }, [activeRows]);

  const isDirty = useMemo(() => JSON.stringify(problems) !== JSON.stringify(original), [problems, original]);
  const hasErrors = Object.keys(validationErrors).length > 0;

  const handleChange = (id, field, value) => {
    setProblems(prev => prev.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  const handleAddRow = () => {
    const newRow = {
      id: -Date.now(),
      clazz: '',
      nr: 0,
      color: 'black',
      description: '',
      deleted: false
    };
    setProblems(prev => [newRow, ...prev]);
  };

  const handleDelete = (id) => {
    if (id < 0) {
      setProblems(prev => prev.filter(p => p.id !== id));
    } else {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hour = String(now.getHours()).padStart(2, '0');
      const minute = String(now.getMinutes()).padStart(2, '0');
      const timestamp = `${year}-${month}-${day} ${hour}:${minute}`;
      setProblems(prev => prev.map(row => 
        row.id === id ? { ...row, deleted: true, deletedAt: timestamp } : row
      ));
    }
  };

  const handleRestore = (id) => {
     setProblems(prev => prev.map(row => 
        row.id === id ? { ...row, deleted: false, deletedAt: undefined } : row
      ));
  };
  
  const handleDiscard = () => {
    if (window.confirm("Discard all changes?")) {
      setProblems(JSON.parse(JSON.stringify(original)));
    }
  };

  const handleSave = async () => {
    if (!accessToken || hasErrors) return;
    try {
      const result = await postContestProblems(accessToken, problems);
      if (result) {
        setProblems(result);
        setOriginal(JSON.parse(JSON.stringify(result)));
      }
    } catch (error) {
      console.error(error);
      alert('Failed to save changes');
    }
  };

  const getCellStyle = (row, field) => {
    const rowErrors = validationErrors[row.id];
    const originalRow = original.find(r => r.id === row.id);
    const isDuplicate = rowErrors?.some(e => e.includes("Duplicate"));
    const searchField = field === 'clazz' ? 'class' : field;
    const isReqError = rowErrors?.some(e => e.toLowerCase().includes(searchField));
    
    const baseStyle = { 
        fontSize: { xs: '0.6rem', md: '0.9rem' }, 
        padding: { xs: '6px 4px', md: '16px' }
    };

    if (isReqError || (isDuplicate && (field === 'clazz' || field === 'nr'))) {
      return { 
        style: { ...baseStyle, backgroundColor: 'rgba(211, 47, 47, 0.25)', transition: '0.3s' },
        error: rowErrors.join(', ')
      };
    }
    if (!originalRow) {
      return { style: { ...baseStyle, backgroundColor: 'rgba(76, 175, 80, 0.15)' } }; 
    }
    if (originalRow[field] !== row[field]) {
      return { style: { ...baseStyle, backgroundColor: 'rgba(33, 150, 243, 0.15)', transition: '0.3s' } }; 
    }
    return { style: baseStyle };
  };

  if (authLoading || (accessToken && !isAdmin && loadingData)) return <Loading />;
  if (!isAdmin && !authLoading && accessToken) return <div>You do not have permission.</div>;

  return (
    <Box sx={{ width: '100%' }}> 
      
      <Paper 
        elevation={2} 
        sx={{ 
          position: 'sticky', 
          top: 0, 
          zIndex: 10,
          mb: 1, 
          p: { xs: 1.5, md: 2 },
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" color="info" startIcon={<AddIcon />} onClick={handleAddRow}>
            Add
          </Button>
          <Button size="small" variant="outlined" color="secondary" startIcon={<RestoreFromTrashIcon />} onClick={() => setRestoreOpen(true)} disabled={deletedRows.length === 0}>
            Trash ({deletedRows.length})
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" variant="outlined" color="error" startIcon={<UndoIcon />} onClick={handleDiscard} disabled={!isDirty}>
            Discard
          </Button>
          <Button size="small" variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDirty || hasErrors}>
            Save
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} elevation={3}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="22%" sx={{ fontWeight: 'bold', fontSize: { xs: '0.6rem', md: '0.875rem' }, padding: { xs: '6px 4px', md: '16px' } }}>Class</TableCell>
              <TableCell width="12%" sx={{ fontWeight: 'bold', fontSize: { xs: '0.6rem', md: '0.875rem' }, padding: { xs: '6px 4px', md: '16px' } }}>No</TableCell>
              <TableCell width="18%" sx={{ fontWeight: 'bold', fontSize: { xs: '0.6rem', md: '0.875rem' }, padding: { xs: '6px 4px', md: '16px' } }}>Color</TableCell>
              <TableCell width="38%" sx={{ fontWeight: 'bold', fontSize: { xs: '0.6rem', md: '0.875rem' }, padding: { xs: '6px 4px', md: '16px' } }}>Description</TableCell>
              <TableCell width="10%" align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '0.6rem', md: '0.875rem' }, padding: { xs: '6px 4px', md: '16px' } }}>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeRows.map((row) => {
              const wrapCell = (field, component) => {
                const { style, error } = getCellStyle(row, field);
                return (
                  <TableCell sx={style}>
                    <Tooltip title={error || ''} arrow placement="top">
                      <Box>
                        {component}
                        {error && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                             <ErrorOutlineIcon color="error" sx={{ fontSize: 12, mr: 0.5 }} />
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                )
              };

              return (
                <TableRow key={row.id} hover>
                  {wrapCell('clazz', 
                    <DebouncedAutocomplete
                      options={existingClasses}
                      value={row.clazz}
                      onChange={(val) => handleChange(row.id, 'clazz', val)}
                      renderInput={(params) => (
                        <TextField 
                            {...params} 
                            variant="standard"
                            sx={compactInputStyle}
                            placeholder="Class" 
                            error={!!validationErrors[row.id]?.some(e => e.includes('Class'))}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: row.clazz ? (
                                    <Box 
                                        component="span" 
                                        sx={{ 
                                            width: 8, height: 8, 
                                            bgcolor: getClassColor(row.clazz), 
                                            borderRadius: '50%', 
                                            mr: 0.5,
                                            flexShrink: 0
                                        }} 
                                    />
                                ) : null
                            }}
                        />
                      )}
                    />
                  )}
                  {wrapCell('nr',
                    <DebouncedTextField
                      fullWidth type="number"
                      value={row.nr}
                      onChange={(val) => handleChange(row.id, 'nr', parseInt(val) || 0)}
                      onFocus={(event) => event.target.select()}
                    />
                  )}
                  {wrapCell('color',
                    <Select
                      fullWidth variant="standard"
                      value={ALLOWED_COLORS.includes(row.color) ? row.color : ''}
                      onChange={(e) => handleChange(row.id, 'color', e.target.value)}
                      sx={{ 
                        fontSize: { xs: '0.6rem', md: '0.9rem' }, 
                        '& .MuiSelect-select': { padding: { xs: '4px 0 5px', md: '6px 0 7px' } } 
                      }}
                    >
                      {ALLOWED_COLORS.map(c => (
                        <MenuItem key={c} value={c} dense>
                           <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box component="span" sx={{ width: 12, height: 12, bgcolor: c, border: '1px solid #ccc', mr: 1, borderRadius: '50%' }} />
                            <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
                                {c.charAt(0).toUpperCase() + c.slice(1)}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  {wrapCell('description',
                    <DebouncedTextField
                      fullWidth
                      value={row.description}
                      onChange={(val) => handleChange(row.id, 'description', val)}
                    />
                  )}
                  <TableCell align="center" sx={{ padding: { xs: '4px', md: '16px' } }}>
                    <IconButton color="error" size="small" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {activeRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="textSecondary">No active problems.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Restore</DialogTitle>
        <DialogContent dividers>
          <List dense>
            {deletedRows.length > 0 ? deletedRows.map((row) => (
              <ListItem key={row.id} divider>
                <ListItemText
                    primary={`${row.clazz}, #${row.nr}, ${row.color}, ${row.description}`} 
                    secondary={`Deleted at: ${row.deletedAt || 'Just now'}`}
                />
                <Button size="small" variant="outlined" onClick={() => handleRestore(row.id)}>Restore</Button>
              </ListItem>
            )) : <Typography align="center" variant="body2" color="textSecondary">Trash is empty.</Typography>}
          </List>
        </DialogContent>
        <DialogActions><Button onClick={() => setRestoreOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default EditProblems;